
import React, { useEffect, useState } from 'react';
import classes from './Products.module.css';
import orderProductIcon from '../../assets/icons/order-product.png';
import gridIcon from '../../assets/icons/gridView.svg';
import listIcon from '../../assets/icons/listView.svg';
import infoIcon from '../../assets/icons/‌info-icon.svg';
import { CustomCalendarModal } from './Modals/CustomCalendarModal';
import { IProductCategory, IProductDetail } from '../../store/slices/productCategories';
import { GridView } from '../../components/UI/GridView/GridView';
import { ProductInformationModal } from './Modals/ProductInformationModal';
import { useAppDispatch } from '../../store/store';
import { clearProductTimeslots } from '../../store/slices/auth';
import { useTranslation } from '../../hooks/useTranslations';
import { getTextByLanguage } from '../../utils/miscUtils';

type Props = {
  productGroups: IProductCategory[];
  products: IProductDetail[];
};

const Products: React.FC<Props> = ({ productGroups, products }) => {
  const [selectedProduct, setSelectedProduct] = useState<IProductDetail>();
  const [selectedProductCategory, setSelectedProductCategory] = useState<IProductCategory | undefined>();
  const [gridView, setGridView] = useState<boolean>(false);
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] = useState(false);
  const [isCustomCalendarModalOpen, setIsCustomCalendarModalOpen] = useState(false);
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const lang = localStorage.getItem('lang') || 'en';

  useEffect(() => {
    if (productGroups?.length > 0) {
      setSelectedProductCategory(productGroups[0]);
    }
    if (!isCustomCalendarModalOpen) {
      dispatch(clearProductTimeslots());
    }
  }, [productGroups, isCustomCalendarModalOpen]);

  const handleProductClick = (product: IProductDetail) => {
    setSelectedProduct(product);
    setIsProductDetailsModalOpen(true);
  };

  const handleAddToCartClick = (event: React.MouseEvent, product: IProductDetail) => {
    event.stopPropagation();
    setSelectedProduct(product);
    setIsCustomCalendarModalOpen(true);
  };

  return (
    <div className={classes.container}>
      <div className={classes.productGroups}>
        {productGroups.map((productGroup) => {
          const categoryName = getTextByLanguage(productGroup.product_category, lang);
          const selectedCategoryName = getTextByLanguage(
            selectedProductCategory?.product_category ?? [],
            lang
          );

          return (
            <div
              key={productGroup.product_category_id}
              className={
                categoryName === selectedCategoryName
                  ? classes.selectedProductGroupItem
                  : classes.productGroupItem
              }
              onClick={() => setSelectedProductCategory(productGroup)}
            >
              {categoryName}
            </div>
          );
        })}
        <div className={classes.gridView} onClick={() => setGridView(!gridView)}>
          <img alt="grid icon" src={gridView ? gridIcon : listIcon} className={classes.gridIcon} />
          <p>{gridView ? 'Grid' : 'List'}</p>
        </div>
      </div>

      <div className={classes.products}>
        <div style={gridView ? { display: 'flex', flexWrap: 'wrap' } : {}}>
          {products
            .filter(
              (product) =>
                getTextByLanguage(product.product_category, lang) ===
                getTextByLanguage(selectedProductCategory?.product_category ?? [], lang)
            )
            .map((product) => {
              const productName = getTextByLanguage(product.name, lang);

              return (
                <div className="wrapper" key={product.product_id}>
                  {gridView ? (
                    <GridView
                      product={product}
                      handleClick={() => handleProductClick(product)}
                      addProductClick={(event) => handleAddToCartClick(event, product)}
                    />
                  ) : (
                    <div className={classes.productItem}>
                      <img
                        alt="product icon"
                        src={
                          product.images && product.images.length > 0
                            ? product.images[0].url
                            : orderProductIcon
                        }
                        className={classes.productIcon}
                      />
                      <div className={classes.productName}>
                        {productName}
                        <div className={classes.informationIcon}>
                          <img
                            src={infoIcon}
                            alt="info-icon"
                            onClick={() => handleProductClick(product)}
                          />
                        </div>
                      </div>

                      <p
                        className={classes.addToCart}
                        onClick={(event) => handleAddToCartClick(event, product)}
                      >
                        {translations('add.to.cart')}
                      </p>
                      <div className={classes.productPrice}>
                        {product.price ? `€ ${(product.price / 100).toFixed(2)}` : '€ 0.00'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {selectedProduct && (
        <ProductInformationModal
          product={selectedProduct}
          isOpen={isProductDetailsModalOpen}
          onClose={() => setIsProductDetailsModalOpen(false)}
        />
      )}
      {selectedProduct && (
        <CustomCalendarModal
          product={selectedProduct}
          isOpen={isCustomCalendarModalOpen}
          onClose={() => setIsCustomCalendarModalOpen(false)}
        />
      )}
    </div>
  );
};

export { Products };

